/**
 * Fits a layout into the cube the camera projects.
 *
 * Every layout here works in whatever units suit it - a force layout's units come out of its
 * spring length, a tree's out of its radial step - and the camera expects roughly [-1, 1] on each
 * axis, as `createProjector` documents. Normalizing at the boundary rather than inside each layout
 * means a layout is judged on its shape and never on its scale, and the camera's zoom means the
 * same thing whichever one is on screen.
 *
 * The scale is uniform across all three axes. A per-axis fit would fill the cube, and it would also
 * report a flat graph as a round one - which for a layered layout, whose whole point is that z
 * carries a different meaning from x and y, is the one distortion that must not happen.
 */

import type { NetworkLayout, Vec3 } from '../types';

export type UnitCubeArgs = {
  layout: NetworkLayout;
  /** Fraction of the cube left empty at the edges, so a node's own radius has somewhere to go. */
  padding: number;
  /**
   * Multiplier on z, applied after the fit.
   *
   * A dial rather than a camera property: `heightScale` on the camera scales the billboard offsets
   * along with the centers, which turns the sphere impostors into ellipses. Stretching the
   * positions here leaves node geometry alone, which is what lets a layered view separate its
   * planes without deforming what sits on them.
   */
  zStretch: number;
};

/**
 * Normalizes a whole layout into the unit cube.
 *
 * Bounds come from the nodes *and* the structure around them, not from the nodes alone. A guide is
 * not decoration that happens to sit among the data - a layer grid is a square, so its corners
 * reach a factor of root two further than any node along the diagonal, and fitting to the nodes
 * only left two corners of every plane hanging outside the viewport. A volume is the same case
 * again and worse: a group's sphere reaches a whole leaf radius past the outermost mark in it, so
 * fitting to the marks crops the outer regions on every side. Routes are deliberately excluded: a
 * bundled spline stays inside the hull of the nodes it joins, and a route that did escape should be
 * clipped rather than allowed to shrink the entire graph.
 */
export const toUnitCube = ({
  layout,
  padding,
  zStretch,
}: UnitCubeArgs): NetworkLayout => {
  const {
    positions,
    links,
    edgeIndices,
    routes,
    guides,
    guideLinks,
    volumes,
    nodeRadii,
  } = layout;
  const bounded = [
    ...positions,
    ...guides.flat(),
    /* The two poles on each axis, which is the sphere's own bounding box. */
    ...volumes.flatMap(({ center: [x, y, z], radius }): Vec3[] => [
      [x - radius, y - radius, z - radius],
      [x + radius, y + radius, z + radius],
    ]),
  ];
  if (bounded.length === 0) return layout;

  let minimumX = Infinity;
  let minimumY = Infinity;
  let minimumZ = Infinity;
  let maximumX = -Infinity;
  let maximumY = -Infinity;
  let maximumZ = -Infinity;

  for (const [x, y, z] of bounded) {
    minimumX = Math.min(minimumX, x);
    minimumY = Math.min(minimumY, y);
    minimumZ = Math.min(minimumZ, z);
    maximumX = Math.max(maximumX, x);
    maximumY = Math.max(maximumY, y);
    maximumZ = Math.max(maximumZ, z);
  }

  const centerX = (minimumX + maximumX) / 2;
  const centerY = (minimumY + maximumY) / 2;
  const centerZ = (minimumZ + maximumZ) / 2;

  /* Governed by the largest extent, so nothing leaves the cube. A degenerate layout - one node, or
     every node coincident - has no extent at all, and the floor keeps the division finite rather
     than sending the single node to infinity. */
  const extent = Math.max(
    1e-6,
    maximumX - minimumX,
    maximumY - minimumY,
    maximumZ - minimumZ
  );
  const scale = (2 * (1 - padding)) / extent;

  const transform = ([x, y, z]: Vec3): Vec3 => [
    (x - centerX) * scale,
    (y - centerY) * scale,
    (z - centerZ) * scale * zStretch,
  ];

  return {
    positions: positions.map(transform),
    links,
    edgeIndices,
    routes: routes.map(route => route.map(transform)),
    guides: guides.map(guide => guide.map(transform)),
    /* Indices, so the fit does not touch them. */
    guideLinks,
    /* The radius takes the uniform scale and not `zStretch`, because a sphere has one radius and a
       stretched z would need an ellipsoid to say what it did. Exact wherever regions are drawn:
       the grouped layout is the only one that produces them and it is not a layout anyone stretches
       - `zStretch` exists to pull a layered stack apart. */
    volumes: volumes.map(volume => ({
      ...volume,
      center: transform(volume.center),
      radius: volume.radius * scale,
    })),
    /* Scaled here rather than resolved by the caller, so a reserved radius and the geometry it was
       reserved against go through exactly one multiplication and cannot drift. */
    nodeRadii: nodeRadii?.map(radius => radius * scale) ?? null,
  };
};
